/* eslint-disable react-refresh/only-export-components */
import { createContext, use, ReactNode, useEffect, useState } from "react";
import { gql } from "@lytra/frontend-common/__generated__/gql";
import { useQuery } from "@apollo/client/react";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { GetProfileQuery } from "@lytra/frontend-common/__generated__/graphql";
import * as Sentry from "@sentry/react";
import { isSessionExpiredError, setLytraSentryContext } from "@lytra/frontend-common";
import Loading from "@/components/Loading";
import { MinimalErrorLayout } from "@/components/MinimalErrorLayout";
import {
  OFFBOARDED_MESSAGE_SETTING_KEY,
  OFFBOARDED_REASON_SETTING_KEY,
  OFFBOARDED_REASON_TESTPHASE,
  OFFBOARDED_SETTING_KEY,
  TenantOffboardedScreen,
} from "@/components/TenantOffboardedScreen";
import { SALES_CONTACT_EMAIL_SETTING_KEY } from "@/lib/salesContact";

const LOAD_TIMEOUT_MS = 15_000;
// How long a profile-query error has to survive before it becomes a full-page error.
const ERROR_GRACE_MS = 2_500;

const PROFILE_QUERY = gql(`
  query GetProfile {
    tenant {
      id
      name
      platform
      serviceEmailAddress
      ticketSystemUrl
      settings {
        key
        value
      }
    }
    adminGetEffectiveSettings {
      key
      value
    }
    user {
      id
      givenName
      familyName
      emailAddress
      isAdmin
      emailSignature
      emailSignatureHtml
    }
    userRoles {
      realmRoles
      tenantRoles
      isLytraStaff
      isAdmin
      isAgent
    }
  }
`);

// `NonNullable` because the provider below refuses to render children until all three
// are present. The schema marks them nullable, and mirroring that here made every
// consumer's `tenant.settings` a possible crash — `useTenantSetting` reads it directly.
// The guarantee is enforced in one place and stated in the type.
interface ProfileContextType {
  tenant: NonNullable<GetProfileQuery["tenant"]>;
  user: NonNullable<GetProfileQuery["user"]>;
  userRoles: NonNullable<GetProfileQuery["userRoles"]>;
  adminGetEffectiveSettings: GetProfileQuery["adminGetEffectiveSettings"];
  /**
   * Offboarded with reason "testphase": the trial is paused, not ended. The app
   * renders normally and only the AI surfaces are withheld (see PausedRouteGuard in
   * router.tsx). False for a full offboarding — that never reaches the tree at all,
   * because the screen below replaces it — and false for Lytra staff, who are exempt.
   */
  tenantPaused: boolean;
  /** Operator-set message shown on the paused notice, if any. */
  tenantPausedMessage?: string;
  /** sales_contact_email, for the paused notice's "write to us" line. */
  tenantPausedContact?: string;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export function ProfileProvider({
  children,
  onSessionExpired,
}: {
  children: ReactNode;
  /** Starts a fresh login. Shown as the way out when the session has ended. */
  onSessionExpired?: () => void;
}) {
  // No `returnPartialData`: it made `data` a deep-partial, and this context promises a
  // complete profile. Every one of the 43 consumers reads `tenant`/`user`/`userRoles`
  // directly — `useTenantSetting` does `tenant.settings.find(...)` — so a partial profile
  // would throw inside them rather than degrade. There is a loading state and an error
  // state right below; "arrived but incomplete" belongs in one of those, not in the tree.
  const { data, loading, error } = useQuery(PROFILE_QUERY, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
    // True so a retry (auth-error link refreshing a token, dev-server restart) shows
    // as loading again instead of leaving the stale error on the result.
    notifyOnNetworkStatusChange: true,
  });

  const [timedOut, setTimedOut] = useState(false);
  // A failed profile query is very often transient — a token refresh, a restarted dev
  // server, a dropped first request — and the retry succeeds within a moment. Painting
  // the full-page "Keine Verbindung" screen on the first failed result made every load
  // flash that error before the content appeared, so an error has to survive
  // ERROR_GRACE_MS before it replaces the loading state. This state holds the error
  // whose window elapsed; comparing identity means a cleared or replaced error is back
  // inside the grace window without needing a reset write.
  const [gracedError, setGracedError] = useState<unknown>(null);

  useEffect(() => {
    if (!loading || data) return;
    const id = setTimeout(() => setTimedOut(true), LOAD_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [loading, data]);

  useEffect(() => {
    if (!error) return;
    const id = setTimeout(() => setGracedError(error), ERROR_GRACE_MS);
    return () => clearTimeout(id);
  }, [error]);
  const errorPersisted = !!error && gracedError === error;

  useEffect(() => {
    if (data?.user) {
      Sentry.setUser({
        id: data.user.id,
        email: data.user.emailAddress ?? undefined,
        username:
          `${data.user.givenName ?? ""} ${data.user.familyName ?? ""}`.trim() ??
          undefined,
      });
    }
    setLytraSentryContext({ tenantId: data?.tenant?.id ?? null });
  }, [data]);

  // Completeness is established once, here, so the context can promise non-null fields
  // instead of 43 consumers each having to cope with a half-loaded profile. Every field
  // the schema marks nullable is checked; `adminGetEffectiveSettings` is not, because
  // useFeature/useFeatureEnabled already treat "no settings" as a valid empty list.
  const profile =
    data?.tenant && data.user && data.userRoles
      ? {
          tenant: data.tenant,
          user: data.user,
          userRoles: data.userRoles,
          adminGetEffectiveSettings: data.adminGetEffectiveSettings,
        }
      : null;

  // Show loading state while fetching initial data — and while an error is still
  // inside its grace window, since the retry usually lands there.
  if (!profile && !timedOut && (loading || (error && !errorPersisted))) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  // Show error state once the failure outlived the grace window, or on timeout (which
  // is the opposite case: a request that never settles at all).
  if (((error && errorPersisted) || timedOut) && !profile) {
    // Apollo Client 4 hands us a plain error rather than v3's ApolloError, so the
    // old `error.networkError !== null` compared `undefined !== null` and classified
    // *every* failure as a network problem — a GraphQL error showed "Keine
    // Verbindung" instead of its own message. Anything that is not a GraphQL error
    // from the server is a transport failure.
    // An ended login session is neither: the server answered, the token is dead and
    // could not be renewed (the auth-error link says so, or the raw response is a 401).
    // "Keine Verbindung" sends people to the status page for a problem only a new login fixes.
    const isSessionExpired =
      !!error &&
      (isSessionExpiredError(error) || (error as { statusCode?: number }).statusCode === 401);
    const isNetworkError = error ? !CombinedGraphQLErrors.is(error) : true;
    const isTimeout = timedOut && !error;
    return (
      <MinimalErrorLayout>
        <div className="max-w-md mx-auto text-center p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {isSessionExpired
              ? "Sitzung abgelaufen"
              : isTimeout
                ? "Verbindung fehlgeschlagen"
                : isNetworkError
                  ? "Keine Verbindung"
                  : "Daten konnten nicht geladen werden"}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {isSessionExpired
              ? "Ihre Anmeldung ist abgelaufen. Bitte melden Sie sich erneut an."
              : isTimeout
                ? "Der Server antwortet nicht. Bitte prüfen Sie Ihre Netzwerkverbindung oder ob ein Zertifikatsfehler vorliegt."
                : isNetworkError
                  ? "Verbindung zum Server nicht möglich. Möglicherweise liegt ein Netzwerkproblem oder eine Wartung vor."
                  : error?.message}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() =>
                isSessionExpired && onSessionExpired ? onSessionExpired() : window.location.reload()
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {isSessionExpired && onSessionExpired ? "Erneut anmelden" : "Erneut versuchen"}
            </button>
            {!isSessionExpired && (
              <a
                href="https://status.lytra.ai/status/lytra"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-blue-600 dark:text-blue-400 border border-blue-600 dark:border-blue-400 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              >
                Systemstatus prüfen
              </a>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            Wenn das Problem weiterhin besteht, wenden Sie sich bitte an den Support.
          </p>
        </div>
      </MinimalErrorLayout>
    );
  }

  // Resolved without an error but still incomplete: keep waiting rather than handing a
  // half-populated profile to the tree.
  if (!profile) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  // Offboarded tenant: replace the entire app with the goodbye notice, so users see
  // why Lytra stopped working instead of an app that silently no longer answers.
  // Deliberately placed after the loading/error branches — a failed profile query must
  // keep showing the connection error, not a wrong "your contract ended" message.
  // Lytra staff are exempt: they impersonate offboarded tenants to export data or
  // check state, and the goodbye screen would lock them out of their own tooling.
  const effective = profile.adminGetEffectiveSettings ?? [];
  const settingValue = (key: string) =>
    effective.find((s) => s.key === key)?.value ??
    profile.tenant.settings.find((s) => s.key === key)?.value;
  const offboarded =
    !profile.userRoles.isLytraStaff &&
    settingValue(OFFBOARDED_SETTING_KEY) === "true";
  // A paused trial is not a churned customer: it keeps its data and every page that
  // reads it, and loses only the AI surfaces. So only a full offboarding replaces the
  // app here; a pause falls through and is enforced per route instead.
  const paused =
    offboarded &&
    settingValue(OFFBOARDED_REASON_SETTING_KEY) === OFFBOARDED_REASON_TESTPHASE;
  if (offboarded && !paused) {
    return (
      <TenantOffboardedScreen
        tenantName={profile.tenant.name}
        message={settingValue(OFFBOARDED_MESSAGE_SETTING_KEY)}
        contactEmail={settingValue(SALES_CONTACT_EMAIL_SETTING_KEY)}
        reason={settingValue(OFFBOARDED_REASON_SETTING_KEY)}
      />
    );
  }

  return (
    <ProfileContext
      value={{
        ...profile,
        tenantPaused: paused,
        tenantPausedMessage: settingValue(OFFBOARDED_MESSAGE_SETTING_KEY),
        tenantPausedContact: settingValue(SALES_CONTACT_EMAIL_SETTING_KEY),
      }}
    >
      {children}
    </ProfileContext>
  );
}

export function useProfile() {
  const context = use(ProfileContext);
  if (context === undefined) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
}

// Utility function to easily check if a feature is enabled (via effective settings)
export function useFeature(featureName: string): boolean {
  const context = use(ProfileContext);
  if (context === undefined) {
    throw new Error("useFeature must be used within a ProfileProvider");
  }

  // Use effective settings which includes global settings as fallback
  const effectiveSettings = context.adminGetEffectiveSettings || [];
  const setting = effectiveSettings.find((s) => s.key === `feature.${featureName}`);
  return setting?.value === 'true';
}

// Like useFeature but supports a custom default when no setting is stored
export function useFeatureEnabled(featureName: string, defaultValue: boolean): boolean {
  const context = use(ProfileContext);
  if (context === undefined) {
    throw new Error("useFeatureEnabled must be used within a ProfileProvider");
  }

  const effectiveSettings = context.adminGetEffectiveSettings || [];
  const setting = effectiveSettings.find((s) => s.key === `feature.${featureName}`);
  if (!setting) return defaultValue;
  return setting.value === 'true';
}

// Reads an effective setting as a boolean by its EXACT key (no "feature."
// prefix, unlike useFeature) — for non-feature settings such as
// "output.single-linebreak". Parses the value identically to the backend's
// get_effective_setting_bool (jupiter/services/settings.py) so the frontend and
// backend never disagree on the same setting.
export function useEffectiveSettingBool(key: string, defaultValue = false): boolean {
  const context = use(ProfileContext);
  if (context === undefined) {
    throw new Error("useEffectiveSettingBool must be used within a ProfileProvider");
  }
  const effectiveSettings = context.adminGetEffectiveSettings || [];
  const setting = effectiveSettings.find((s) => s.key === key);
  if (!setting) return defaultValue;
  return ["true", "1", "yes", "on", "enabled"].includes(setting.value.toLowerCase());
}

// Utility function to get a tenant setting
export function useTenantSetting(key: string): string | undefined {
  const { tenant } = useProfile();
  const setting = tenant.settings.find((setting) => setting.key === key);
  return setting?.value;
}
